import type { ReactNode } from 'react'
import { cn } from '../../utils/helpers'

interface CardProps {
  children: ReactNode
  className?: string
  title?: string
  subtitle?: string
  action?: ReactNode
  variant?: 'default' | 'blue' | 'green' | 'purple' | 'orange'
}

export function Card({ children, className, title, subtitle, action, variant = 'default' }: CardProps) {
  const variantClasses = {
    default: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
    blue: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30',
    green: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30',
    purple: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30',
    orange: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30',
  }

  const headerVariantClasses = {
    default: 'border-gray-200 dark:border-gray-700',
    blue: 'border-blue-200 dark:border-blue-800 bg-blue-100 dark:bg-blue-900/40',
    green: 'border-green-200 dark:border-green-800 bg-green-100 dark:bg-green-900/40',
    purple: 'border-purple-200 dark:border-purple-800 bg-purple-100 dark:bg-purple-900/40',
    orange: 'border-orange-200 dark:border-orange-800 bg-orange-100 dark:bg-orange-900/40',
  }

  return (
    <div
      className={cn(
        'rounded-lg border shadow-sm',
        variantClasses[variant],
        className
      )}
    >
      {(title || subtitle || action) && (
        <div className={cn(
          'flex items-center justify-between border-b px-6 py-4',
          headerVariantClasses[variant]
        )}>
          <div>
            {title && (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      <div className="p-6">{children}</div>
    </div>
  )
}

export function CardHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col space-y-1.5 p-6', className)}>
      {children}
    </div>
  )
}

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn('text-2xl font-semibold leading-none tracking-tight', className)}>
      {children}
    </h3>
  )
}

export function CardContent({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('p-6 pt-0', className)}>{children}</div>
}
